/**
 * Frontend telemetry service for error tracking.
 *
 * Sends frontend errors to the backend, which logs them to Application Insights.
 * Uses fire-and-forget pattern - failures are logged but don't affect user experience.
 *
 * Architecture: Frontend → Backend API → Application Insights
 * This centralises telemetry in the backend, avoiding an extra SDK dependency.
 */

export interface ErrorTelemetry {
  /** Error message */
  message: string
  /** Error stack trace (if available) */
  stack?: string
  /** Error type/name (e.g., 'ChunkLoadError', 'TypeError') */
  errorType: string
  /** Component or module where error occurred */
  source: string
  /** Additional context data */
  context?: Record<string, unknown>
  /** User's current session ID (if available) */
  sessionId?: string
  /** Timestamp of error */
  timestamp: string
}

/**
 * Get the API base URL from environment or default.
 */
function getApiBaseUrl(): string {
  return (import.meta.env['VITE_API_BASE_URL'] as string | undefined) ?? ''
}

/**
 * Track an error for telemetry.
 *
 * Fire-and-forget: failures are logged to console but don't throw.
 * This ensures error tracking never disrupts user experience.
 */
export async function trackError(telemetry: ErrorTelemetry): Promise<void> {
  // Always log to console for local debugging
  console.error('[Telemetry] Error tracked:', {
    message: telemetry.message,
    errorType: telemetry.errorType,
    source: telemetry.source,
    sessionId: telemetry.sessionId,
  })

  try {
    const baseUrl = getApiBaseUrl()

    // Send to backend telemetry endpoint (fire-and-forget)
    // Using fetch instead of axios to avoid circular dependencies
    await fetch(`${baseUrl}/api/v1/telemetry/errors`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(telemetry),
      // Short timeout - we don't want to slow down error recovery
      signal: AbortSignal.timeout(5000),
    })
  } catch (error) {
    // Silent fail - don't let telemetry errors affect the user
    // Include original telemetry for local debugging when backend is unavailable
    console.warn('[Telemetry] Failed to send error telemetry (non-blocking):', {
      sendError: error,
      originalError: {
        message: telemetry.message,
        errorType: telemetry.errorType,
        source: telemetry.source,
      },
    })
  }
}

/**
 * Create error telemetry from an Error object.
 *
 * Helper to standardise error capture.
 */
export function createErrorTelemetry(
  error: Error,
  source: string,
  context?: Record<string, unknown>,
  sessionId?: string
): ErrorTelemetry {
  return {
    message: error.message,
    stack: error.stack,
    errorType: error.name || 'Error',
    source,
    context,
    sessionId,
    timestamp: new Date().toISOString(),
  }
}

/**
 * Track a chunk load error (lazy loading failure).
 *
 * Common in SPAs when deployment changes chunk hashes.
 */
export function trackChunkLoadError(
  error: Error,
  componentName: string,
  sessionId?: string
): Promise<void> {
  return trackError(
    createErrorTelemetry(
      error,
      `LazyLoad:${componentName}`,
      {
        userAgent: navigator.userAgent,
        url: window.location.href,
        isChunkError: error.message.includes('Loading chunk') ||
                      error.message.includes('Failed to fetch dynamically imported module'),
      },
      sessionId
    )
  )
}

/**
 * Track a context error (React context not available).
 */
export function trackContextError(
  error: Error,
  contextName: string,
  sessionId?: string
): Promise<void> {
  return trackError(
    createErrorTelemetry(
      error,
      `Context:${contextName}`,
      {
        url: window.location.href,
      },
      sessionId
    )
  )
}

/**
 * Track an API error (failed API request).
 */
export function trackApiError(
  error: Error,
  endpoint: string,
  sessionId?: string,
  statusCode?: number
): Promise<void> {
  return trackError(
    createErrorTelemetry(
      error,
      `API:${endpoint}`,
      {
        statusCode,
        url: window.location.href,
      },
      sessionId
    )
  )
}
